import type { PortNumberRemap } from './portNamingSchemes';

export type { PortNumberRemap };

export function portCustomLabelKey(number: number, face?: string | null): string {
  return face ? `${face}:${number}` : String(number);
}

export function getPortCustomLabel(
  labels: Record<string, string> | undefined,
  number: number,
  face?: string | null
): string {
  if (!labels) return '';
  if (face) {
    const keyed = labels[`${face}:${number}`];
    if (typeof keyed === 'string') return keyed;
  }
  const fallback = labels[String(number)];
  return typeof fallback === 'string' ? fallback : '';
}

export function setPortCustomLabel(
  labels: Record<string, string> | undefined,
  number: number,
  value: string,
  face?: string | null
): Record<string, string> {
  const next = { ...(labels || {}) };
  const key = portCustomLabelKey(number, face);
  if (!value) delete next[key];
  else next[key] = value;
  return next;
}

function remapNumber(
  remaps: PortNumberRemap[],
  number: number,
  face?: string
): number {
  const remap = face
    ? remaps.find((r) => r.face === face) || remaps.find((r) => !r.face)
    : remaps.find((r) => !r.face) || remaps[0];
  const mapped = remap?.map[number];
  return mapped != null ? mapped : number;
}

/** Rewrite custom-label keys when the layout editor remaps port numbers. */
export function remapPortCustomLabels(
  labels: Record<string, string> | undefined,
  remaps: PortNumberRemap[]
): Record<string, string> | undefined {
  if (!labels || Object.keys(labels).length === 0 || !remaps?.length) return labels;

  const next: Record<string, string> = {};
  let changed = false;

  for (const [key, value] of Object.entries(labels)) {
    const faceMatch = key.match(/^([^:]+):(\d+)$/);
    if (faceMatch) {
      const face = faceMatch[1];
      const oldNum = parseInt(faceMatch[2], 10);
      const newNum = Number.isFinite(oldNum) ? remapNumber(remaps, oldNum, face) : oldNum;
      const newKey = `${face}:${newNum}`;
      if (newKey !== key) changed = true;
      next[newKey] = value;
      continue;
    }
    if (/^\d+$/.test(key)) {
      const oldNum = parseInt(key, 10);
      const newNum = Number.isFinite(oldNum) ? remapNumber(remaps, oldNum) : oldNum;
      const newKey = String(newNum);
      if (newKey !== key) changed = true;
      next[newKey] = value;
      continue;
    }
    next[key] = value;
  }

  return changed ? next : labels;
}

export function customLabelsForFace(
  labels: Record<string, string> | undefined,
  ports: Array<{ number: number }>,
  face?: string | null
): Record<number, string> {
  const out: Record<number, string> = {};
  if (!labels) return out;
  for (const port of ports) {
    const value = getPortCustomLabel(labels, port.number, face);
    if (value) out[port.number] = value;
  }
  return out;
}
